/* eslint-disable react/forbid-prop-types */
import React, { useState, useEffect, useRef, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import PropTypes from 'prop-types';
import { useMutation } from 'react-query';
import moment from 'moment';
import ModalBodyLayout from '../../../shared/ModalBodyLayout';
import { YIELD_OFFER_DETAILS } from '../../mutations';
import { currencies, exchange, walletTypes } from '../../../../utilities/constants';
import {
  formatDecimalNumber,
  formatNumber,
  formatNumberWithComma,
} from '../../../../utilities/formatHelpers';
import FocusableInputArea from '../../../Accounts/partials/FocusableInputArea';
import { validateNumbers } from '../../../../utilities/formValidation';
import { MODAL_SCREENS } from '../../constants';
import { GenericLoading } from '../../../../components/Placeholders';

const YieldDeposit = ({ details, openScreen, rate, setConfirmedOffer, wallet }) => {
  const { t: translate } = useTranslation();

  const [offerDetails, setOfferDetails] = useState({});
  const [duration, setDuration] = useState(0);
  const [timer, setTimer] = useState(0);

  const [minAmount, setMinAmount] = useState(details?.minAmount);
  const [maxAmount, setMaxAmount] = useState(details?.minAmount);

  const [depositAmount, setDepositAmount] = useState(formatNumberWithComma(minAmount));
  const [depositAmountDecimal, setDepositAmountDecimal] = useState(minAmount);

  const [offerError, setOfferError] = useState(false);

  const availableUSDCBalance = wallet?.available_balance * currencies.USDC.conversion;

  const [depositAmountInputWidth, setDepositAmountInputWidth] = useState(0);

  const depositAmountInputSpan = useRef();
  const timerRef = useRef(null);
  const callOfferEndpointTimerRef = useRef();

  const { mutate: getOfferDetails, isLoading: isLoadingOfferDetails } = useMutation(
    YIELD_OFFER_DETAILS,
    {
      onSettled(data, error) {
        if (!error) {
          setOfferError(false);
          setOfferDetails(data);
          const currentDate = moment();
          const expiryDate = moment(data?.product?.expirationDate);
          const days = expiryDate.diff(currentDate, 'days');

          const effectiveDate = moment(data?.contractAttributes?.effectiveDate);
          const effectiveTime = effectiveDate.diff(currentDate, 'seconds');

          setTimer(effectiveTime);
          setDuration(days);

          setMinAmount(data?.product?.minAmount);
          setMaxAmount(data?.product?.maxAmount);
        } else {
          setOfferError(true);
        }
        clearTimeout(callOfferEndpointTimerRef.current);
      },
    }
  );

  useEffect(() => {
    timerRef.current = setTimeout(() => {
      if (timer > 0) setTimer(timer - 1);
    }, 1000);

    return () => {
      clearTimeout(timerRef.current);
    };
  }, [offerDetails, timer]);

  const btnDisabled = useMemo(
    () =>
      timer === 0 ||
      depositAmountDecimal < minAmount ||
      depositAmountDecimal > maxAmount ||
      depositAmountDecimal > availableUSDCBalance ||
      isLoadingOfferDetails,
    [timer, isLoadingOfferDetails, depositAmountDecimal, minAmount, maxAmount, availableUSDCBalance]
  );

  useEffect(() => {
    if (
      details &&
      timer === 0 &&
      !offerError &&
      !isLoadingOfferDetails &&
      depositAmountDecimal > 0
    ) {
      getOfferDetails({ productId: details?.id, amount: parseFloat(depositAmountDecimal) });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [timer, isLoadingOfferDetails, offerError]);

  const sliderDisabled = maxAmount === minAmount;

  let sliderValue;
  if (sliderDisabled) {
    sliderValue = minAmount;
  } else if (depositAmountDecimal < minAmount) {
    sliderValue = minAmount;
  } else if (depositAmountDecimal > maxAmount) {
    sliderValue = maxAmount;
  } else {
    sliderValue = parseFloat(depositAmountDecimal);
  }

  const handleChangeDepositAmount = (event) => {
    const { value: amount, min } = event.target;
    const { decimalAmount, formattedAmount } = formatDecimalNumber(amount, min);

    if (!validateNumbers(decimalAmount, walletTypes.CRYPTO)) {
      return;
    }

    if (!decimalAmount || !formattedAmount) {
      setDepositAmount(0);
    }

    setDepositAmount(formattedAmount);
    setDepositAmountDecimal(decimalAmount);
  };

  const handleFocus = (e) => {
    if (!e.target) return;
    e.target.select();
    if (e.target.closest('.amount-input')) {
      e.target.closest('.amount-input').classList.add('active');
    }
  };

  const handleBlurAmount = (e) => {
    if (!e.target) return;
    if (e.target.closest('.amount-input')) {
      e.target.closest('.amount-input').classList.remove('active');
    }
  };

  useEffect(() => {
    const charactersLength =
      depositAmountInputSpan?.current?.value.length > 1
        ? depositAmountInputSpan?.current?.value.length
        : 1;
    // to avoid having empty input, fix length to 1 if text length is less then 1
    setDepositAmountInputWidth(charactersLength);
  }, [depositAmount]);

  const renderTitle = () => (
    <div className="w-100 d-flex flex-row justify-content-between align-items-center">
      <p className="text-dark text-f5">{translate('yield_offer_details_modal_title')}</p>
      <div className="d-flex flex-row">
        <p className="text-f6 text-shadow me-2">{translate('oxygen_yield_current_BTC_label')}</p>
        <p className="text-f6 text-dark">{`${formatNumberWithComma(
          formatNumber(rate?.price, exchange.FIAT_VALUE)
        )} ${currencies.USDC.fullName}`}</p>
      </div>
    </div>
  );

  const renderFooter = () =>
    !offerError ? (
      <>
        <div className="d-flex flex-row justify-content-center mb-4">
          <p className="text-f6 text-shadow">
            {translate('yield_qoute_label').replace('__TIMER__', timer)}
          </p>
        </div>
        <button
          className={`btn ${!btnDisabled ? 'btn-primary' : 'btn-disabled'}`}
          type="button"
          disabled={btnDisabled}
          onClick={() => {
            setConfirmedOffer({
              amount: depositAmountDecimal,
              productId: details?.id,
            });
            openScreen(MODAL_SCREENS.CONFRIM_DEPOSIT);
          }}>
          {translate('continue_to_checkout_btn_label')}
        </button>
      </>
    ) : (
      <button
        className="btn btn-primary"
        type="button"
        onClick={() => {
          setOfferError(false);
          clearTimeout(timerRef.current);
          setDepositAmount(minAmount ? formatNumberWithComma(minAmount) : 0);
          setDepositAmountDecimal(minAmount);
          getOfferDetails({ productId: details?.id, amount: minAmount });
        }}>
        {translate('try_again_btn_title')}
      </button>
    );

  const handleOnKeyUp = () => {
    if (depositAmountDecimal >= minAmount) {
      callOfferEndpointTimerRef.current = setTimeout(() => {
        getOfferDetails({ productId: details?.id, amount: depositAmountDecimal });
      }, 2000);
    }
  };

  const handleOnMouseUpSlider = () => {
    if (depositAmountDecimal >= minAmount) {
      callOfferEndpointTimerRef.current = setTimeout(() => {
        getOfferDetails({ productId: details?.id, amount: depositAmountDecimal });
      }, 500);
    }
  };

  const handleEnterMaxAmount = () => {
    setDepositAmount(availableUSDCBalance ? formatNumberWithComma(availableUSDCBalance) : 0);
    setDepositAmountDecimal(availableUSDCBalance);

    getOfferDetails({
      productId: details?.id,
      amount: availableUSDCBalance,
    });
  };

  return (
    <ModalBodyLayout
      withBack={false}
      renderTitle={renderTitle}
      renderFooter={renderFooter}
      isScrollable>
      <div className="d-flex flex-row justify-content-between mt-4">
        <div className="d-flex flex-column">
          <p className="text-primary text-f4 text-bold">
            {`${formatNumber(
              offerDetails?.contractAttributes?.annualizedYieldPct,
              exchange.FIAT_VALUE
            )}%`}
          </p>
          <p className="text-shadow text-f6">
            {translate('yield_offer_details_modal_annualized_label')}
          </p>
        </div>
        <div className="d-flex flex-column align-items-end">
          <p className="text-dark text-f4 text-bold">
            {translate('yield_offer_details_modal_days_label').replace('__DAYS_NUMBER__', duration)}
          </p>
          <p className="text-shadow text-f6">
            {translate('yield_offer_details_modal_duration_label')}
          </p>
        </div>
      </div>
      <div className="d-flex flex-row justify-content-between mt-4">
        <div className="d-flex flex-column">
          <p className="text-dark text-f5 text-bold">
            {offerDetails && offerDetails?.product?.linkedPrice
              ? `${formatNumberWithComma(
                  formatNumber(offerDetails?.product?.linkedPrice, exchange.FIAT_VALUE)
                )} ${currencies.USDC.fullName}`
              : `${formatNumberWithComma(formatNumber(0, exchange.FIAT_VALUE))} ${
                  currencies.USDC.fullName
                }`}
          </p>
          <p className="text-shadow text-f6">
            {translate('yield_offer_details_modal_linked_price_label')}
          </p>
        </div>
        <div className="d-flex flex-column align-items-end">
          <p className="text-dark text-f5 text-bold">
            {offerDetails &&
              offerDetails?.product?.expirationDate &&
              moment(offerDetails?.product?.expirationDate, 'YYYYMMDDh:mm:ss').format('DD/MM/YYYY')}
          </p>
          <p className="text-shadow text-f6">
            {translate('yield_offer_details_modal_expiry_date_label')}
          </p>
        </div>
      </div>
      {!offerError && (
        <div className="mt-4">
          <p className="text-shadow text-f5">
            {translate('yield_offer_details_modal_enter_amount_label')}
          </p>
          <div className="d-flex flex-row justify-content-center align-items-center mt-3">
            <p className="text-f6 text-shadow text-bold me-2">
              {`${translate('available_balance_label')}:`}
            </p>
            <div
              role="button"
              tabIndex="-1"
              onClick={handleEnterMaxAmount}
              onKeyDown={handleEnterMaxAmount}>
              <p className="text-dark text-f6 text-bold">
                {`${formatNumberWithComma(
                  formatNumber(availableUSDCBalance, exchange.FIAT_VALUE)
                )} ${currencies.USDC.fullName}`}
              </p>
            </div>
          </div>
          <FocusableInputArea className="h1 amount-input mt-2">
            <input
              required
              type="text"
              className="form-control bg-transparent text-bold p-0 text-center border-0"
              value={depositAmount}
              style={{
                fontSize: 'inherit',
                width: `${depositAmountInputWidth + 1}ch`,
                color: 'inherit',
                height: 'inherit',
                textIndent: depositAmount ? 0 : '34%',
              }}
              ref={depositAmountInputSpan}
              onChange={(e) => handleChangeDepositAmount(e)}
              onFocus={handleFocus}
              onBlur={handleBlurAmount}
              onKeyUp={handleOnKeyUp}
              placeholder="0  "
            />
            <b
              style={{
                fontSize: 'inherit',
                pointerEvents: 'none',
                color: 'inherit',
              }}>
              {currencies.USDC.fullName}
            </b>
          </FocusableInputArea>
          <div className="mb-2 position-relative">
            <div className="slider-progress">
              <div className="progress">
                <div
                  className="progress-bar"
                  style={{
                    width: `${
                      !sliderDisabled
                        ? ((sliderValue - minAmount) * 100) / (maxAmount - minAmount)
                        : 0
                    }%`,
                  }}
                />
              </div>
              <input
                type="range"
                className="form-range"
                value={parseFloat(depositAmountDecimal)}
                max={maxAmount}
                min={minAmount}
                step={1}
                disabled={false}
                onChange={(e) => {
                  if (!sliderDisabled) {
                    handleChangeDepositAmount(e);
                  }
                }}
                onMouseUp={() => {
                  if (!sliderDisabled) {
                    handleOnMouseUpSlider();
                  }
                }}
              />
            </div>
            {/* this lines for implementing edges for the slider */}
            <div
              className="position-absolute w-100 d-flex flex-row justify-content-between align-items-start"
              style={{ top: '15%' }}>
              {Array.from({ length: 7 }, (e, i) => i + 1).map((item) => (
                <hr
                  key={item}
                  className={`progress-edges text-disabled ${
                    item === 1 || item === 7 ? 'progress-main-edge' : 'progress-edges'
                  }`}
                  style={{ transform: 'rotate(90deg)', height: '1rem !important' }}
                />
              ))}
              {Array.from({ length: 6 }, (e, i) => i + 1).map((item) => (
                <hr
                  key={item}
                  className={`progress-edges text-disabled ${
                    item === 6 ? 'progress-main-edge' : 'progress-edges'
                  }`}
                  style={{ transform: 'rotate(90deg)', height: '1rem !important' }}
                />
              ))}
              {Array.from({ length: 6 }, (e, i) => i + 1).map((item) => (
                <hr
                  key={item}
                  className={`progress-edges text-disabled ${
                    item === 6 ? 'progress-main-edge' : 'progress-edges'
                  }`}
                  style={{ transform: 'rotate(90deg)', height: '1rem !important' }}
                />
              ))}
              {Array.from({ length: 6 }, (e, i) => i + 1).map((item) => (
                <hr
                  key={item}
                  className={`progress-edges text-disabled ${
                    item === 6 ? 'progress-main-edge' : 'progress-edges'
                  }`}
                  style={{ transform: 'rotate(90deg)', height: '1rem !important' }}
                />
              ))}
            </div>
            <div className="position-absolute w-100 d-flex flex-row justify-content-between align-items-start">
              <p className="text-f6 text-disabled">
                {formatNumberWithComma(formatNumber(minAmount, exchange.FIAT_VALUE))}
              </p>
              <p className="text-f6 text-disabled">
                {formatNumberWithComma(formatNumber(maxAmount, exchange.FIAT_VALUE))}
              </p>
            </div>
          </div>
          {isLoadingOfferDetails && (
            <div>
              <GenericLoading isFullScreen={false} customSize="4rem" customSpacing="pt-4 mt-4" />
            </div>
          )}
          {!isLoadingOfferDetails && (
            <>
              <p className="text-shadow text-f6 text-bold mt-5">
                {translate('yield_contirm_invest_received_amount_note')}
              </p>
              <div className="d-flex flex-column bg-light rounded-extra p-3 mt-2">
                <div className="d-flex flex-row justify-content-between">
                  <div className="d-flex flex-row justify-content-start align-items-center">
                    <div className="w-25 me-2">
                      <img
                        src={`./assets/icons/${currencies.USDC.fullName}.svg`}
                        className="w-100"
                        alt={currencies.USDC.fullName}
                      />
                    </div>
                    <p className="text-dark text-nowrap">{`${formatNumberWithComma(
                      formatNumber(
                        offerDetails?.contractAttributes?.usdcPaidAmount,
                        exchange.FIAT_VALUE
                      )
                    )} ${currencies.USDC.fullName}`}</p>
                  </div>
                  <p className="text-dark text-nowrap">{`${formatNumberWithComma(
                    formatNumber(offerDetails?.contractAttributes?.usdcProfit, exchange.FIAT_VALUE)
                  )} ${currencies.USDC.fullName}`}</p>
                </div>
                <div className="d-flex flex-row justify-content-between">
                  <p className="text-shadow text-f6">
                    {translate('yield_offer_details_modal_get_token_more_note').replace(
                      '__PRICE__',
                      formatNumberWithComma(
                        formatNumber(offerDetails?.product?.linkedPrice, exchange.FIAT_VALUE)
                      )
                    )}
                  </p>
                  <p className="text-shadow text-f6">
                    {translate('yield_offer_details_modal_profit')}
                  </p>
                </div>

                <div className="d-flex flex-row justify-content-center align-items-center my-2">
                  <hr className="m-0 text-shadow w-100" />
                  <p className="text-shadow mx-2">{translate('yield_offer_details_modal_or')}</p>
                  <hr className="m-0 text-shadow w-100" />
                </div>

                <div className="d-flex flex-row justify-content-between">
                  <div className="d-flex flex-row justify-content-start align-items-center">
                    <div className="w-25 me-2">
                      <img
                        src={`./assets/icons/${currencies.BTC.icon}.svg`}
                        className="w-100"
                        alt={currencies.USDC.fullName}
                      />
                    </div>
                    <p className="text-dark text-nowrap">{`${formatNumberWithComma(
                      formatNumber(
                        offerDetails?.contractAttributes?.btcPaidAmount,
                        currencies.BTC.decimal
                      )
                    )} BTC`}</p>
                  </div>
                  <p className="text-dark text-nowrap">{`${formatNumberWithComma(
                    formatNumber(offerDetails?.contractAttributes?.btcBuyAt, exchange.FIAT_VALUE)
                  )} ${currencies.USDC.fullName}`}</p>
                </div>
                <div className="d-flex flex-row justify-content-between">
                  <p className="text-shadow text-f6">
                    {translate('yield_offer_details_modal_get_token_less_note').replace(
                      '__PRICE__',
                      formatNumberWithComma(
                        formatNumber(offerDetails?.product?.linkedPrice, exchange.FIAT_VALUE)
                      )
                    )}
                  </p>
                  <p className="text-shadow text-f6">
                    {translate('yield_offer_details_modal_buy_at_label')}
                  </p>
                </div>
              </div>
            </>
          )}
        </div>
      )}
      {offerError && (
        <div className="d-flex flex-column justify-content-center align-items-center">
          <div className="text-center mt-5">
            <img className="w-100 mb-2" src="./assets/images/warning.png" alt="Success" />
          </div>
          <p className="text-dark text-f5 text-center text-bold mt-2">
            {translate('offer_error_msg')}
          </p>
        </div>
      )}
    </ModalBodyLayout>
  );
};

export default YieldDeposit;

YieldDeposit.propTypes = {
  wallet: PropTypes.object,
  details: PropTypes.object.isRequired,
  rate: PropTypes.object,
  openScreen: PropTypes.func.isRequired,
  setConfirmedOffer: PropTypes.func.isRequired,
};

YieldDeposit.defaultProps = {
  wallet: {},
  rate: {},
};
